import { ChangeDetectionStrategy, Component, Inject, OnInit, signal, WritableSignal } from "@angular/core";
import { AsyncPipe, CommonModule, NgClass } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { SkeletonModule } from "primeng/skeleton";
import { DynamicDialogConfig } from "primeng/dynamicdialog";
import { BehaviorSubject, combineLatest, map, Observable } from "rxjs";
import { FormGroup } from "@angular/forms";
import {
  appSettingsRestClientInjectionToken,
  IApp,
  IApplicationSettingsGroupField,
  IAppSettings,
  IAppSettingsRestClient,
  IGetApplicationSettingsForm
} from "@zaeper/angular-application-store-lib";
import {
  ESeverity,
  FormBuilderComponent,
  InputGroup,
  IToastService,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import {
  TFieldElement,
  TInputValue
} from "@zaeper/angular-dashboard-view-lib";
import { IReceiver } from "../../models/i-receiver";
import { AppSettingsInputFieldMapper } from "../../mappers/app-settings-field.mapper";

interface IAppSettingsModalData {
  readonly app: IApp;
  readonly receiver: IReceiver;
  readonly onSaveCallback?: () => void;
}

@Component({
  selector: "app-app-settings-modal",
  standalone: true,
  imports: [AsyncPipe, CommonModule, NgClass, ButtonModule, SkeletonModule, FormBuilderComponent],
  templateUrl: "./app-settings-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppSettingsModalComponent implements OnInit {
  public readonly isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly isSaving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly groups$: BehaviorSubject<IApplicationSettingsGroupField[]> = new BehaviorSubject<IApplicationSettingsGroupField[]>([]);
  public readonly selectedGroupId: WritableSignal<string | null> = signal(null);
  public readonly formFields$: Observable<TFieldElement[] | null>;

  private readonly _selectedGroupId$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private readonly _app: IApp;
  private readonly _receiver: IReceiver;
  private readonly _onSaveCallback: (() => void) | null;
  private readonly _formGroup: FormGroup = new FormGroup({});

  constructor(
    @Inject(appSettingsRestClientInjectionToken) private readonly _appSettingsRestClient: IAppSettingsRestClient,
    @Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
    private readonly _dynamicDialogConfig: DynamicDialogConfig<IAppSettingsModalData>
  ) {
    this._app = this._dynamicDialogConfig.data!.app;
    this._receiver = this._dynamicDialogConfig.data!.receiver;
    this._onSaveCallback = this._dynamicDialogConfig.data?.onSaveCallback ?? null;

    this.formFields$ = combineLatest([this.groups$, this._selectedGroupId$]).pipe(
      map(([groups, selectedGroupId]: [IApplicationSettingsGroupField[], string | null]): TFieldElement[] | null => {
        if (!selectedGroupId) {
          return null;
        }
        const group: IApplicationSettingsGroupField | undefined = groups.find((candidate: IApplicationSettingsGroupField) => candidate.id === selectedGroupId);
        if (!group) {
          return null;
        }

        const children: TFieldElement[] = new AppSettingsInputFieldMapper(this._formGroup).from(group);
        return [new InputGroup({ groupId: group.id, children })];
      })
    );
  }

  public ngOnInit(): void {
    this._loadAppSettings();
  }

  public selectGroup(groupId: string): void {
    this.selectedGroupId.set(groupId);
    this._selectedGroupId$.next(groupId);
  }

  public groupLabel(group: IApplicationSettingsGroupField): string {
    const label = group.label;
    if (!label) {
      return group.id;
    }
    return (label as unknown as Record<string, string>)["de"]
      ?? (label as unknown as Record<string, string>)["en"]
      ?? group.id;
  }

  public handleOnChange(_values: Map<string, TInputValue>): void {
    // Values are tracked through the shared FormGroup; persistence happens via handleSave().
  }

  public handleSave(): void {
    // TODO: wire up a saveApplicationSettings endpoint once the app-settings API exposes it.
    this._toastService.setMessage({
      title: "Noch nicht verfügbar",
      detail: "Das Speichern der App-Einstellungen ist aktuell noch nicht implementiert.",
      severity: ESeverity.info
    });
    this._onSaveCallback?.();
  }

  private _loadAppSettings(): void {
    this.isLoading$.next(true);
    const getApplicationSettingsForm: IGetApplicationSettingsForm = {
      applicationId: this._app.id,
      receiverId: this._receiver.id
    };

    this._appSettingsRestClient.getApplicationSettings(getApplicationSettingsForm)
      .then((appSettings: IAppSettings) => {
        const groups: IApplicationSettingsGroupField[] = AppSettingsInputFieldMapper.rootGroups(appSettings);
        this.groups$.next(groups);
        if (groups.length > 0) {
          this.selectGroup(groups[0].id);
        }
      })
      .catch(() => {
        this._toastService.setMessage({
          title: "Fehler",
          detail: "Die Einstellungen konnten nicht geladen werden.",
          severity: ESeverity.error
        });
        this.groups$.next([]);
      })
      .finally(() => {
        this.isLoading$.next(false);
      });
  }
}
